'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';

type Notification = {
  type: string;
  handle: string;
  duration: number;
  substitutions: Record<string, string>;
};

enum ActionType {
  NOTIFICATION_CREATED,
  NOTIFICATION_REMOVED,
}

type Action =
  | {
      type: ActionType.NOTIFICATION_CREATED;
      data: Notification;
    }
  | {
      type: ActionType.NOTIFICATION_REMOVED;
      data: { handle: string };
    };

type NotificationsContextType = {
  notifications: Array<Notification>;
  removeNotification: (handle: string, cb?: () => void) => void;
  createNotification: (
    type: string,
    handle: string,
    substitutions?: Record<string, string>,
    duration?: number,
    cb?: () => void
  ) => void;
};

const reducer = (state: Array<Notification>, action: Action) => {
  switch (action.type) {
    case ActionType.NOTIFICATION_CREATED: {
      return [...state, action.data];
    }

    case ActionType.NOTIFICATION_REMOVED: {
      return state.filter(({ handle }) => handle !== action.data.handle);
    }
  }
};

const NotificationsContext = createContext<NotificationsContextType | null>(
  null
);

const initialState: Array<Notification> = [];

interface Props {
  children: ReactNode;
}

const NotificationsProvider = ({ children }: Props) => {
  const [notifications, dispatch] = useReducer(reducer, initialState);

  const createNotification = useCallback(
    (
      type: string,
      handle: string,
      substitutions = {},
      duration = 5000,
      cb: (handle: string) => void = () => {}
    ) => {
      const data = { type, handle, duration, substitutions };
      dispatch({ type: ActionType.NOTIFICATION_CREATED, data });

      cb(handle);
    },
    [dispatch]
  );

  const removeNotification = useCallback(
    (handle: string, cb = () => {}) => {
      const data = { handle };
      dispatch({ type: ActionType.NOTIFICATION_REMOVED, data });

      cb();
    },
    [dispatch]
  );

  const value = useMemo(
    () => ({ createNotification, removeNotification, notifications }),
    [createNotification, removeNotification, notifications]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) throw new Error('useNotifications requires a context!');

  return context;
}

export { NotificationsProvider, useNotifications };
